import {Injectable} from '@angular/core';
import {
  MerchantDiscount,
  CreateMerchantDiscountRequest,
  Paged,
  PagedRequest, AffiliateMerchantDiscount, DiscountSyncLog
} from '../../dtos/api';
import {environment} from '../../../../environments/environment';
import {DashboardHttpService} from '../../../core-services/dashboard-http-service/dashboard-http.service';
import {map, switchMap, tap} from 'rxjs/operators';
import {deepMutateIsoToMtTime, mtTimeToUtc} from '../../pure-utils/string-utils';
import {forkJoin, Observable, of} from 'rxjs';
import {SortDirection} from '@angular/material/sort';
@Injectable({
  providedIn: 'root'
})
export class DiscountApiService {
  constructor(private http: DashboardHttpService) { }

  getPagedMerchantDiscounts(merchantId: string, pagedRequest?: PagedRequest) {
    let params = { ...pagedRequest,
      sort: pagedRequest.sort && pagedRequest.direction
        ? pagedRequest.sort === 'discount_rule_type_name' ? 'drt.' + pagedRequest.sort : 'dv.' + pagedRequest.sort : 'dv.started_at',
      direction: pagedRequest.direction ?  pagedRequest.direction.toUpperCase() : 'DESC',
      page: pagedRequest.page?.toString(),
      limit: pagedRequest.limit?.toString()
    };

    return this.http.getWithOptions<Paged<MerchantDiscount>>(`${environment.apiUrl}/merchants/${merchantId}/discounts`, {params}).pipe(
      tap(x => deepMutateIsoToMtTime(x))
    );
  }

  getAllMerchantDiscounts(merchantId: string, sort?: string, direction?: SortDirection) {
    let params = {page: 1, limit: 1000} as PagedRequest;
    params = (sort && direction) ? {...params, sort, direction} : params;
    return this.getPagedMerchantDiscounts(merchantId, params).pipe(
      switchMap(initialResponse => {
        let total_pages = Math.ceil(initialResponse.total_items / initialResponse.items_per_page);
        let requests: Observable<Paged<MerchantDiscount>>[] = [];
        for (let i = 2; i <= total_pages; i++) {
          requests.push(this.getPagedMerchantDiscounts(merchantId, {...params, page: i}));
        }

        if (requests.length > 0) {
          return forkJoin(requests).pipe(
            map(forkResponse => initialResponse.items.concat(...forkResponse.map(x => x.items)))
          );
        }

        return of(initialResponse.items);
      })
    );
  }

  getAffiliateDiscounts(affiliateId: string, pagedRequest?: PagedRequest, seeRestricted?: Boolean) {
    let params = { ...pagedRequest,
      sort: pagedRequest.sort && pagedRequest.direction
        ? pagedRequest.sort === 'discount_rule_type_name' ? 'drt.' + pagedRequest.sort : pagedRequest.sort === 'merchant_name' ? 'm.' + pagedRequest.sort
          : 'dv.' + pagedRequest.sort : 'dv.started_at',
      direction: pagedRequest.direction ?  pagedRequest.direction.toUpperCase() : 'DESC',
      page: pagedRequest.page?.toString(),
      limit: pagedRequest.limit?.toString(),
      exclude_discount_rule_type_id: seeRestricted ? '' : 'ac581f0e-e419-4d86-a45f-2fb5a1d490e2'
    };

    return this.http.getWithOptions<Paged<AffiliateMerchantDiscount>>(`${environment.apiUrl}/affiliates/${affiliateId}/discounts`, {params}).pipe(
      tap(x => deepMutateIsoToMtTime(x))
    );
  }

  getAffiliateMerchantDiscountById(affiliateId: string, merchantDiscountId: string) {
    return this.http.get<MerchantDiscount>(`${environment.apiUrl}/affiliates/${affiliateId}/discounts/${merchantDiscountId}`);
  }

  createMerchantDiscount(merchantId: string, createEmailRequest: CreateMerchantDiscountRequest) {
    let createEmailRequestFinal = {...createEmailRequest, started_at: createEmailRequest.started_at !== 'now'
        ? mtTimeToUtc(createEmailRequest.started_at).toISOString() : createEmailRequest.started_at};
    if (createEmailRequest?.expired_at) {
      createEmailRequestFinal.expired_at = createEmailRequest.expired_at !== 'now' ? mtTimeToUtc(createEmailRequest.expired_at.toISOString())
        : createEmailRequest.expired_at;
    }

    return this.http.postWithOptions<MerchantDiscount>(`${environment.apiUrl}/merchants/${merchantId}/discounts`, createEmailRequestFinal).pipe(
      tap(x => deepMutateIsoToMtTime(x))
    );
  }

  updateMerchantDiscount(merchantId: string, merchantDiscountId: string, updateEmailRequest: Partial<CreateMerchantDiscountRequest>) {
    let createEmailRequestFinal = {...updateEmailRequest};
    if (updateEmailRequest?.started_at) {
      createEmailRequestFinal.started_at = updateEmailRequest.started_at !== 'now' ? mtTimeToUtc(updateEmailRequest.started_at.toISOString())
        : updateEmailRequest.started_at;
    }
    if (updateEmailRequest?.expired_at) {
      createEmailRequestFinal.expired_at = updateEmailRequest.expired_at !== 'now' ? mtTimeToUtc(updateEmailRequest.expired_at.toISOString())
      : updateEmailRequest.expired_at;
    }

    return this.http.putWithOptions<MerchantDiscount>(`${environment.apiUrl}/merchants/${merchantId}/discounts/${merchantDiscountId}`, createEmailRequestFinal)
      .pipe(tap(x => deepMutateIsoToMtTime(x)));
  }

  deleteMerchantDiscount(merchantId: string, merchantDiscountId: string) {
    return this.http.delete(`${environment.apiUrl}/merchants/${merchantId}/discounts/${merchantDiscountId}`);
  }

  deleteMultipleMerchantDiscounts(merchantId: string, discounts: MerchantDiscount[]) {
    let deletePayload = discounts.map((discount: MerchantDiscount) => ({discount_id: discount.discount_id}));
    return this.http.deleteWithBody<{request: string}, MerchantDiscount[]>(`${environment.apiUrl}/merchants/${merchantId}/discounts`,
      {request: JSON.stringify(deletePayload)}).pipe(
      tap(x => deepMutateIsoToMtTime(x))
    );
  }

  updateDiscountsShopifySync(merchantId: string, discountSync: boolean) {
    return this.http.postWithOptions<{success: boolean}>(`${environment.apiUrl}/merchants/${merchantId}/discounts/sync`, {discount_sync: discountSync});
  }

  getPagedMerchantDiscountSyncLogs(merchantId: string, pagedRequest?: PagedRequest) {
    let params = { ...pagedRequest,
      sort: pagedRequest.sort && pagedRequest.direction ? 'dsl.' + pagedRequest.sort : 'dsl.sort_updated_at',
      direction: pagedRequest.direction ?  pagedRequest.direction.toUpperCase() : 'DESC',
      page: pagedRequest.page?.toString(),
      limit: pagedRequest.limit?.toString()
    };

    return this.http.getWithOptions<Paged<DiscountSyncLog>>(`${environment.apiUrl}/merchants/${merchantId}/discounts/logs`, {params}).pipe(
      tap(x => deepMutateIsoToMtTime(x))
    );
  }
}
